import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, CheckCircle, Clock, Download, LogIn, RefreshCw, Search, UserX
} from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import { apiService } from '../../services/api';
import '../../styles/mentee-dashboard.scss';
import '../plan/plan.scss';
import '../../styles/admin-analytics.scss';

interface Usuario {
  user_id: number;
  nombre: string;
  username: string;
  email: string;
  rol: 'mentor' | 'mentee';
  is_test: number;
  contraparte: string | null;
  ultimo_ingreso: string | null;
  cambio_clave: boolean;
  entro: boolean;
  acuerdo_firmado: string | null;
  foda_estado: 'draft' | 'finalized' | null;
  foda_items: number;
  pdi_estado: 'draft' | 'finalized' | null;
  pdi_objetivos: number;
  pdi_acciones: number;
  pdi_completadas: number;
  pdi_avance: number;
  sesiones_agendadas: number;
  sesiones_realizadas: number;
  mensajes: number;
  comentarios: number;
  ultima_actividad: string | null;
}

interface Resumen {
  total: number; mentores: number; mentees: number;
  entraron: number; nunca_entraron: number; cambiaron_clave: number;
  acuerdos_firmados: number; foda_finalizados: number; foda_en_curso: number;
  pdi_iniciados: number; sesiones_agendadas: number; sesiones_realizadas: number;
}

type Filtro = 'todos' | 'nunca' | 'entraron' | 'mentor' | 'mentee';

const fecha = (d?: string | null) => {
  if (!d) return null;
  const f = new Date(String(d).replace(' ', 'T'));
  return isNaN(f.getTime()) ? String(d) : f.toLocaleDateString('es', { day: '2-digit', month: 'short' })
    + ' · ' + f.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
};

const haceCuanto = (d?: string | null) => {
  if (!d) return null;
  const dias = Math.floor((Date.now() - new Date(String(d).replace(' ', 'T')).getTime()) / 86400000);
  if (isNaN(dias)) return null;
  return dias <= 0 ? 'hoy' : dias === 1 ? 'ayer' : `hace ${dias} días`;
};

const AdminAnalytics: React.FC = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    try {
      setCargando(true);
      const r = await apiService.get('/admin/analytics/usage');
      setUsuarios(r.data?.usuarios || []);
      setResumen(r.data?.resumen || null);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo cargar la analítica');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const visibles = usuarios.filter(u => {
    if (filtro === 'nunca' && u.entro) return false;
    if (filtro === 'entraron' && !u.entro) return false;
    if ((filtro === 'mentor' || filtro === 'mentee') && u.rol !== filtro) return false;
    if (!busqueda.trim()) return true;
    const q = busqueda.toLowerCase();
    return u.nombre.toLowerCase().includes(q) || u.username.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  const exportar = () => {
    const cabecera = ['Nombre', 'Usuario', 'Correo', 'Rol', 'Dupla', 'Último ingreso', 'Cambió clave',
      'Acuerdo firmado', 'FODA', 'Ítems FODA', 'PDI objetivos', 'PDI acciones', 'PDI avance %',
      'Sesiones agendadas', 'Sesiones realizadas', 'Mensajes', 'Última actividad'];

    const filas = visibles.map(u => [
      u.nombre, u.username, u.email, u.rol, u.contraparte || '',
      u.ultimo_ingreso || 'nunca', u.cambio_clave ? 'sí' : 'no',
      u.acuerdo_firmado || 'no', u.foda_estado || 'sin empezar', u.foda_items,
      u.pdi_objetivos, u.pdi_acciones, u.pdi_avance,
      u.sesiones_agendadas, u.sesiones_realizadas, u.mensajes, u.ultima_actividad || '',
    ]);

    const csv = [cabecera, ...filas]
      .map(f => f.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `uso-plataforma-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const chips: { key: Filtro; label: string }[] = [
    { key: 'todos', label: `Todos (${usuarios.length})` },
    { key: 'nunca', label: `Nunca ingresaron (${usuarios.filter(u => !u.entro).length})` },
    { key: 'entraron', label: `Ingresaron (${usuarios.filter(u => u.entro).length})` },
    { key: 'mentor', label: `Mentores (${usuarios.filter(u => u.rol === 'mentor').length})` },
    { key: 'mentee', label: `Mentees (${usuarios.filter(u => u.rol === 'mentee').length})` },
  ];

  return (
    <MainLayout>
      <div className="mentee-dashboard sessions-view">
        <div className="sv-eyebrow">Administración</div>
        <h1 className="sv-title">Uso de la <em>Plataforma</em></h1>
        <p className="sv-lead">
          Quién entró, quién no lo ha hecho todavía y hasta dónde avanzó cada participante en su plan.
        </p>

        <div className="sessions-page">
          {error && <div className="sf-empty">{error}</div>}

          {cargando ? (
            <p className="dash-empty">Cargando analítica…</p>
          ) : (
            <>
              {resumen && (
                <div className="an-kpis">
                  <Kpi valor={`${resumen.entraron}/${resumen.total}`} etiqueta="Han ingresado" icono={<LogIn size={15} />} />
                  <Kpi valor={resumen.nunca_entraron} etiqueta="Nunca ingresaron" icono={<UserX size={15} />}
                    alerta={resumen.nunca_entraron > 0} />
                  <Kpi valor={resumen.acuerdos_firmados} etiqueta="Acuerdos firmados" icono={<CheckCircle size={15} />} />
                  <Kpi valor={resumen.foda_finalizados} etiqueta="FODA finalizados" />
                  <Kpi valor={resumen.pdi_iniciados} etiqueta="PDI iniciados" />
                  <Kpi valor={`${resumen.sesiones_realizadas}/${resumen.sesiones_agendadas}`} etiqueta="Sesiones hechas" />
                </div>
              )}

              <div className="an-toolbar">
                <div className="res-chips">
                  {chips.map(c => (
                    <button key={c.key} className={`dupla-chip ${filtro === c.key ? 'on' : ''}`}
                      onClick={() => setFiltro(c.key)}>{c.label}</button>
                  ))}
                </div>
                <div className="an-toolbar-right">
                  <div className="an-search">
                    <Search size={14} />
                    <input className="sf-input" placeholder="Buscar persona…"
                      value={busqueda} onChange={e => setBusqueda(e.target.value)} />
                  </div>
                  <button className="btn-ghost" onClick={cargar}><RefreshCw size={14} /> Actualizar</button>
                  <button className="btn-ghost" onClick={exportar}><Download size={14} /> CSV</button>
                </div>
              </div>

              {visibles.length === 0 ? (
                <p className="dash-empty">Nadie coincide con el filtro.</p>
              ) : (
                <div className="an-tabla-wrap">
                  <table className="an-tabla">
                    <thead>
                      <tr>
                        <th>Participante</th>
                        <th>Último ingreso</th>
                        <th>Acuerdo</th>
                        <th>FODA</th>
                        <th>PDI</th>
                        <th>Sesiones</th>
                        <th>Actividad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibles.map(u => <Fila key={u.user_id} u={u} />)}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

const Kpi: React.FC<{ valor: React.ReactNode; etiqueta: string; icono?: React.ReactNode; alerta?: boolean }> =
  ({ valor, etiqueta, icono, alerta }) => (
    <div className={`an-kpi ${alerta ? 'an-kpi--alerta' : ''}`}>
      <div className="an-kpi-valor">{valor}</div>
      <div className="an-kpi-lbl">{icono} {etiqueta}</div>
    </div>
  );

const Fila: React.FC<{ u: Usuario }> = ({ u }) => {
  // El sistema crea la etapa en cuanto alguien abre el plan; sin ítems, no hay avance real.
  const fodaTexto = u.foda_estado === 'finalized' ? 'Finalizado'
    : u.foda_items > 0 ? `Borrador · ${u.foda_items} ítems`
      : 'Sin empezar';

  const pdiTexto = u.pdi_objetivos > 0
    ? `${u.pdi_objetivos} objetivos · ${u.pdi_completadas}/${u.pdi_acciones} acciones`
    : 'Sin empezar';

  return (
    <tr className={u.entro ? '' : 'an-fila--inactiva'}>
      <td>
        <div className="an-persona">
          <span className="an-nombre">{u.nombre}</span>
          <span className={`an-rol an-rol--${u.rol}`}>{u.rol === 'mentor' ? 'Mentor' : 'Mentee'}</span>
          {!!u.is_test && <span className="an-rol an-rol--test">prueba</span>}
        </div>
        <div className="an-sub">{u.username} · {u.contraparte ? `con ${u.contraparte}` : 'sin dupla'}</div>
      </td>

      <td>
        {u.entro ? (
          <>
            <div className="an-dato">{fecha(u.ultimo_ingreso)}</div>
            <div className="an-sub">{haceCuanto(u.ultimo_ingreso)}{u.cambio_clave ? ' · cambió su clave' : ' · clave inicial'}</div>
          </>
        ) : (
          <span className="an-nunca"><AlertTriangle size={13} /> Nunca ingresó</span>
        )}
      </td>

      <td>{u.acuerdo_firmado
        ? <span className="an-si"><CheckCircle size={13} /> {fecha(u.acuerdo_firmado)}</span>
        : <span className="an-no">Pendiente</span>}
      </td>

      <td>
        <span className={u.foda_estado === 'finalized' ? 'an-si' : u.foda_items > 0 ? 'an-medio' : 'an-no'}>
          {fodaTexto}
        </span>
      </td>

      <td>
        <span className={u.pdi_objetivos > 0 ? 'an-medio' : 'an-no'}>{pdiTexto}</span>
        {u.pdi_acciones > 0 && (
          <div className="an-barra"><div className="an-barra-fill" style={{ width: `${u.pdi_avance}%` }} /></div>
        )}
      </td>

      <td>
        {u.sesiones_agendadas > 0
          ? <span className="an-dato">{u.sesiones_realizadas} de {u.sesiones_agendadas}</span>
          : <span className="an-no">Ninguna</span>}
      </td>

      <td>
        <div className="an-sub">
          {u.ultima_actividad ? <><Clock size={11} /> {haceCuanto(u.ultima_actividad)}</> : '—'}
        </div>
        {(u.mensajes > 0 || u.comentarios > 0) && (
          <div className="an-sub">
            {u.mensajes > 0 && `${u.mensajes} mensaje(s)`}
            {u.mensajes > 0 && u.comentarios > 0 && ' · '}
            {u.comentarios > 0 && `${u.comentarios} comentario(s)`}
          </div>
        )}
      </td>
    </tr>
  );
};

export default AdminAnalytics;
